import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINT } from "../../../../constants/constants";
import useGetFormEdit from "./useGetFormEdit";
import MuPb from "../../../../widgets/MuPb";
import { getErrorResponse } from "../../../../utils2024/utils";
import CountryStateCity from "./widgets/CountryStateCity";
import SelectDropDown from "./widgets/SelectDropdown";
import {
  X as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { UserContext } from "../../../context/UserContext";
import { getHeader, getUser } from "../../../../utility/utility";
import { toast } from "react-toastify";
import CategorySelectorAccordions from "../../common/CategorySelectorAccordions";
import BackBtn from "../../../../widgets/BackBtn";
const getIcon = (type, props = {}) => {
  switch (type.toLowerCase()) {
    case "x":
      return <TwitterIcon {...props} />;
    case "fb":
      return <FacebookIcon {...props} />;
    case "ln":
      return <LinkedInIcon {...props} />;
    case "in":
      return <InstagramIcon {...props} />;
    case "phone":
      return <PhoneIcon {...props} />;
    case "email":
      return <EmailIcon {...props} />;
    default:
      return null;
  }
};
function toCamelCase(str = "") {
  return str
    .replace(/[^a-zA-Z0-9\s_-]/g, "") // Remove special characters except spaces, underscores, and hyphens
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+|[-_]+/g, ""); // Remove spaces, underscores, and hyphens
}

const phoneFieldKeys = ["mobileNo", "mobileNumber", "phone", "phoneNumber"];
const isPhoneField = (field) =>
  field.isPhoneField ||
  phoneFieldKeys.includes(toCamelCase(field.properties?.label));

const getProfileFieldKey = (field, user) => {
  const key = toCamelCase(field.properties?.label);
  if (!isPhoneField(field) || user[key] !== undefined) return key;
  return phoneFieldKeys.find((name) => user[name] !== undefined) || "mobileNo";
};

const RegFormContainer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, loading, reloadUser } = useContext(UserContext);
  const hasSetInitialData = useRef(false);
  const [visitorData, setVisitorData] = useState({
    country: { name: "India", code: "IN" },
    state: null, // Adjusted structure
    city: null,
  });
  const {
    data: formItems,
    isLoading: isFormLoading,
    isError: isFormError,
    error: formError,
  } = useGetFormEdit();
  const [formFields, setFormFields] = useState([]);

  useEffect(() => {
    const initializeForm = () => {
      if (!formItems?.form || !user || hasSetInitialData.current) return;

      const newVisitorData = { ...visitorData };
      const updatedFormFields = formItems.form.map((field) => {
        const properties = field.properties || {};

        if (field.type === "CountryStateCity") {
          newVisitorData.country = user.country || {
            name: "India",
            code: "IN",
          };
          newVisitorData.state = user.state || null;
          newVisitorData.city = user.city || null;
          return field;
        }

        const fieldKey = getProfileFieldKey(field, user);
        const userValue = user[fieldKey];

        if (userValue !== undefined) {
          newVisitorData[fieldKey] = userValue;
          return {
            ...field,
            properties: { ...properties, value: userValue },
          };
        }

        return field;
      });

      setVisitorData(newVisitorData);
      setFormFields(updatedFormFields);
      hasSetInitialData.current = true;
    };

    initializeForm();
  }, [formItems?.form, user]);

  if (loading || isFormLoading) {
    return <MuPb />;
  }
  if (isFormError) {
    return <Alert severity="error">{getErrorResponse(formError)}</Alert>;
  }
  const validateForm = (fields) => {
    const errors = [];
    let isValid = true;

    fields.forEach((field) => {
      const properties = field.properties || {};
      const { label, value, required } = properties; // Destructure from "properties"

      if (!required || field.label === "Country/State/City") return;

      let isInvalid = false;

      // Check for null or undefined
      if (value === null || value === undefined) {
        isInvalid = true;
      }
      // Check for empty string (after trimming)
      else if (typeof value === "string") {
        isInvalid = value.trim() === "";
      }
      // Check for empty array
      else if (Array.isArray(value)) {
        isInvalid = value.length === 0;
      }
      // Check for empty object
      else if (typeof value === "object") {
        isInvalid = Object.keys(value).length === 0;
      }

      if (isInvalid) {
        isValid = false;
        errors.push(`${label} is required`);
      }
    });

    return { isValid, errors };
  };

  const renderFormElement = (field) => {
    const { type, properties } = field;
    switch (type) {
      case "TextField":
        return (
          <TextField
            disabled={properties.label === "Email"}
            type={isPhoneField(field) ? "tel" : properties.type}
            label={properties.label}
            required={properties.required}
            multiline={properties.multiline}
            rows={properties.rows}
            value={properties.value ?? ""}
            onChange={(event) => {
              const newV = event.target.value;
              updateFieldProperties(field.id, { value: newV });
              const id = getProfileFieldKey(field, user);
              setVisitorData({ ...visitorData, [id]: newV });
            }}
            InputProps={
              properties.iconed && properties.iconType
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        {getIcon(properties.iconType, {
                          fontSize: "small",
                          color: "action",
                        })}
                      </InputAdornment>
                    ),
                  }
                : null
            }
            fullWidth
            variant="outlined"
            size="small"
          />
        );
      case "PlainText":
        return (
          <Typography variant={properties.variant}>
            {properties.label}
          </Typography>
        );
      case "Divider":
        return <Divider />;
      case "Select":
        return (
          <SelectDropDown
            value={properties.value}
            onChange={(newV) => {
              updateFieldProperties(field.id, { value: newV });
              const id = getProfileFieldKey(field, user);
              setVisitorData({ ...visitorData, [id]: newV });
            }}
            properties={properties}
          />
        );
      case "ProductCategories":
        return (
          <CategorySelectorAccordions
            selectedCategories={
              visitorData[toCamelCase(properties.label)] || []
            }
            title={`${properties.label}${properties.required ? "*" : ""}`}
            onSelectionChange={(newV) => {
              updateFieldProperties(field.id, { value: newV });
              const id = getProfileFieldKey(field, user);
              setVisitorData({ ...visitorData, [id]: newV });
            }}
            isOpen={true}
          />
        );
      case "CountryStateCity":
        return (
          <CountryStateCity
            key={visitorData.country?.code} // Reset on country change
            onChange={(newV) => {
              setVisitorData((prev) => ({
                ...prev,
                ...newV,
              }));
            }}
            initialCountry={visitorData.country}
            initialState={visitorData.state}
            initialCity={visitorData.city}
            countryRequired
            stateRequired
          />
        );
      case "Divider":
        return <Divider />;
      default:
        return <Typography variant="body2">{field.type}</Typography>;
    }
  };
  const updateFieldProperties = (fieldId, newProperties) => {
    setFormFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId
          ? { ...field, properties: { ...field.properties, ...newProperties } }
          : field
      )
    );
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);
    const { isValid, errors } = validateForm(formFields);
    if (!isValid) {
      setSubmissionError(errors.join("\n"));
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...visitorData,
      };

      const response = await fetch(
        `${API_ENDPOINT}/user/update-visitor-profile`,
        {
          method: "PUT",
          ...getHeader(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Update failed");
      }
      reloadUser();
      navigate("/visitor/profile");
    } catch (error) {
      setSubmissionError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Box>
      <BackBtn />
      <Container maxWidth="md">
        <Typography variant="h6" mb={2}>
          Update Profile Details
        </Typography>

        <Grid container spacing={2}>
          {formFields.map((field, index) => (
            <Grid
              item
              xs={
                field?.properties?.grid
                  ? parseInt(field.properties.grid, 10)
                  : 12
              }
            >
              {renderFormElement(field)}
            </Grid>
          ))}
          <Grid item xs={12} container justifyContent="center">
            <Button
              onClick={handleSubmit}
              sx={{ maxWidth: "200px" }}
              type="submit"
              variant="contained"
              color="primary"
              // Remove fullWidth as it will make the button take the full width
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </Grid>
        </Grid>
        {submissionError && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {submissionError}
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default RegFormContainer;
